import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '../utils/dateUtils';
import { getApiBaseUrl } from '../utils/apiConfig';
import { Radio, Space } from 'antd-mobile';
import './MobileEventManagement.css';
import MobileEditMatch from './MobileEditMatch'; // 引入编辑比赛组件
import MatchTagsCompact from './match/MatchTagsCompact'; // 引入紧凑型标签组件
import EventSummary from './event/EventSummary'; // 引入活动总结组件

/**
 * 判断是否是默认比分（0:0）
 * @param {string} scores - 比分字符串，如 "21-19,19-21" 或 "0-0"
 * @returns {boolean} - 是否是默认比分
 */
const isDefaultScore = (scores) => {
  if (!scores) {
    return true;
  }
  
  // 空字符串或 "0-0" 表示默认比分
  if (scores.trim() === '' || scores === '0-0') {
    return true;
  }
  
  // 检查是否所有局都是 0-0（如 "0-0,0-0"）
  const scoreParts = scores.split(',');
  if (scoreParts.length === 0) {
    return true;
  }
  
  // 如果所有局都是 0-0，则是默认比分
  return scoreParts.every(part => {
    const trimmed = part.trim();
    if (trimmed === '0-0') {
      return true;
    }
    const [team1, team2] = trimmed.split('-').map(Number);
    return team1 === 0 && team2 === 0;
  });
};

/**
 * 判断比赛是否已开始（有非0:0的比分）
 * @param {string} scores - 比分字符串
 * @returns {boolean} - 比赛是否已开始
 */
const isMatchStarted = (scores) => {
  return !isDefaultScore(scores);
};

/**
 * 移动端活动管理页面
 * 支持创建活动、添加比赛和手工清算积分
 * 
 * @returns {JSX.Element} 移动端活动管理页面
 */
const MobileEventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventMatches, setEventMatches] = useState([]);
  const [selectedZone, setSelectedZone] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    location: '',
    description: '',
    zone: ''
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { zone } = useParams();
  
  // 定义可用的分区
  const zones = [
    { id: 'sz', name: '深圳' },
    { id: 'gz', name: '广州' }
  ];

  // 添加确认对话框状态
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState(null);
  
  // 编辑比赛相关状态
  const [showEditForm, setShowEditForm] = useState(false);
  const [matchToEdit, setMatchToEdit] = useState(null);
  
  // 批量生成比赛相关状态
  const [showGenerateMatchesForm, setShowGenerateMatchesForm] = useState(false);
  const [showMatchPreview, setShowMatchPreview] = useState(false);
  const [previewMatches, setPreviewMatches] = useState([]);
  const [previewStats, setPreviewStats] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [generateFormData, setGenerateFormData] = useState({
    players: [],
    matchType: 'DOUBLES' // 默认双打
  });

  // 处理分区切换（未使用，已注释）
  // const handleZoneChange = (newZone) => {
  //   setSelectedZone(newZone);
  //   navigate(`/badminton/${newZone}`);
  // };

  useEffect(() => {
    if (zone) {
      setSelectedZone(zone);
      setFormData(prev => ({ ...prev, zone }));
    } else {
      // 如果没有指定分区，默认使用第一个分区
      const defaultZone = zones[0].id;
      setSelectedZone(defaultZone);
      setFormData(prev => ({ ...prev, zone: defaultZone }));
      navigate(`/badminton/${defaultZone}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zone, navigate]);

  // 在组件挂载时检查location.state
  useEffect(() => {
    console.log('[DEBUG] 组件挂载，检查location.state:', location.state);
    if (location.state?.showCreateForm) {
      console.log('[DEBUG] 检测到showCreateForm=true，自动打开创建表单');
      setTimeout(() => {
        setShowCreateForm(true);
        console.log('[DEBUG] 已设置showCreateForm=true');
      }, 0);
    }
  }, [location.state]);
 
  // 监控showCreateForm变化
  useEffect(() => {
    console.log('[DEBUG] showCreateForm值变更为:', showCreateForm);
    // 添加延迟检查，确认状态是否真的已更新
    setTimeout(() => {
      console.log('[DEBUG] 延迟检查showCreateForm:', showCreateForm);
    }, 100);
  }, [showCreateForm]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const baseUrl = getApiBaseUrl();
        console.log('[获取活动列表] 开始获取，zone:', zone, 'URL:', `${baseUrl}/api/${zone}/events/with-stats`);
        const response = await axios.get(`${baseUrl}/api/${zone}/events/with-stats`, {
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        console.log('[获取活动列表] 响应:', response.data);
        
        if (response.data.success) {
          const eventsData = response.data.data || [];
          console.log('[获取活动列表] 获取到活动数量:', eventsData.length);
          eventsData.forEach((e, i) => {
            console.log(`[获取活动列表] 活动${i+1}:`, e.name, 'zone:', e.zone, 'ID:', e._id);
          });
          setEvents(eventsData);
        } else {
          throw new Error(response.data.message || '获取活动数据失败');
        }
        setLoading(false);
      } catch (err) {
        console.error('[获取活动列表] 获取失败:', err);
        setError(err.message || '获取活动数据失败');
        setLoading(false);
      }
    };

    if (zone) {
      fetchEvents();
    }
  }, [zone]);

  // 获取活动的比赛列表
  const fetchEventMatches = async (eventId) => {
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000; // 1秒

    const tryFetch = async () => {
      try {
        console.log('正在获取比赛数据，活动ID:', eventId);
        const baseUrl = getApiBaseUrl();
        const response = await axios.get(`${baseUrl}/api/${zone}/events/${eventId}/matches`);
        
        if (response.data.success) {
          console.log('获取比赛数据成功，原始数据:', response.data.data);
          
          // 数据完整性检查
          const matches = response.data.data.map(match => {
            // 检查必要字段
            if (!match.teams || !match.teams.team1 || !match.teams.team2) {
              console.error('比赛数据缺少必要字段:', match);
            }
            
            // 检查球员数据
            const team1Valid = match.teams?.team1?.every(p => p && p.player && p.player.name);
            const team2Valid = match.teams?.team2?.every(p => p && p.player && p.player.name);
            
            if (!team1Valid || !team2Valid) {
              console.error('球员数据不完整:', {
                matchId: match._id,
                team1: match.teams?.team1,
                team2: match.teams?.team2
              });
            }
            
            return {
              ...match,
              scores: match.scores || '0-0'
            };
          });
          
          console.log('处理后的比赛数据:', matches);
          setEventMatches(matches);
        } else {
          throw new Error(response.data.message || '获取比赛数据失败');
        }
      } catch (err) {
        console.error('获取比赛数据失败:', err);
        console.error('错误详情:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          stack: err.stack
        });

        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`尝试第 ${retryCount} 次重试，等待 ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return tryFetch();
        }

        setError(`获取比赛数据失败: ${err.message}`);
        setEventMatches([]);
      }
    };

    await tryFetch();
  };

  const handleCreateEvent = () => {
    console.log('执行handleCreateEvent函数, 设置showCreateForm=true');
    console.log('[DEBUG] 当前zone值:', zone);
    
    // 强制延迟setState以避免批量更新问题
    setTimeout(() => {
      console.log('[DEBUG] 开始更新状态');
      setShowCreateForm(true);
      setSelectedEvent(null);
      setFormData({
        name: '',
        date: new Date().toISOString().split('T')[0],
        location: '',
        description: '',
        zone: zone || '' // 使用从URL参数获取的zone值
      });
      console.log('[DEBUG] 状态已更新: showCreateForm =', true);
    }, 0);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[创建活动] 表单提交事件触发');
    console.log('[创建活动] zone:', zone);
    console.log('[创建活动] formData:', formData);
    
    // 验证必填字段
    if (!formData.name || !formData.date || !formData.location) {
      console.error('[创建活动] 表单验证失败：缺少必填字段');
      setError('请填写所有必填字段');
      return;
    }
    
    if (!zone) {
      console.error('[创建活动] zone为空，无法创建活动');
      setError('分区信息缺失，无法创建活动');
      return;
    }
    
    try {
        const baseUrl = getApiBaseUrl();
      console.log('[创建活动] 开始创建活动，zone:', zone, 'formData:', formData);
      const response = await axios.post(`${baseUrl}/api/${zone}/events`, formData);
      console.log('[创建活动] 创建响应:', response.data);
      
      if (response.data.success) {
        console.log('[创建活动] 创建成功，开始重新获取活动列表...');
        // 创建成功后，重新获取活动列表（确保数据格式一致，包含统计信息）
        const listResponse = await axios.get(`${baseUrl}/api/${zone}/events/with-stats`, {
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        console.log('[创建活动] 获取列表响应:', listResponse.data);
        
        if (listResponse.data.success) {
          console.log('[创建活动] 获取到活动列表，数量:', listResponse.data.data?.length);
          setEvents(listResponse.data.data || []);
          console.log('[创建活动] 活动列表已更新');
        } else {
          console.warn('[创建活动] 获取列表失败，使用创建返回的数据');
          // 如果获取列表失败，至少添加新创建的活动
          setEvents(prev => [{
            ...response.data.data,
            matchCount: 0,
            playerCount: 0
          }, ...prev]);
        }
        setShowCreateForm(false);
        // 重置表单
        setFormData({
          name: '',
          date: new Date().toISOString().split('T')[0],
          location: '',
          description: '',
          zone: ''
        });
      } else {
        throw new Error(response.data.message || '创建活动失败');
      }
    } catch (err) {
      console.error('[创建活动] 创建失败:', err);
      setError(err.message || '创建活动失败');
    }
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setSelectedEvent(null);
  };

  const handleEventClick = async (event) => {
    setSelectedEvent(event);
    await fetchEventMatches(event._id);
  };

  // 活动总结入口仅保留在活动详情的“活动总结”卡片和活动管理列表中

  const handleAddMatch = (eventId) => {
    // 修改: 不通过URL参数传递eventId，而是存储在sessionStorage中
    sessionStorage.setItem('currentEventId', eventId);
    const currentZone = zone || selectedZone;
    sessionStorage.setItem('currentZone', currentZone);
    console.log("[DEBUG] 添加比赛被点击 - 区域:", currentZone, "活动ID:", eventId);
    // 使用简化的URL导航到添加比赛页面
    navigate(`/badminton/${currentZone}/add-match`);
  };

  // 获取所有球员列表
  const fetchPlayers = async () => {
    try {
        const baseUrl = getApiBaseUrl();
      const response = await axios.get(`${baseUrl}/api/${zone}/players`);
      if (response.data.success) {
        setAvailablePlayers(response.data.data || []);
      }
    } catch (err) {
      console.error('获取球员列表失败:', err);
      setError('获取球员列表失败');
    }
  };

  // 打开批量生成比赛表单
  const handleOpenGenerateMatches = async (eventId) => {
    await fetchPlayers();
    setGenerateFormData({
      players: [],
      matchType: 'DOUBLES' // 默认双打
    });
    setShowGenerateMatchesForm(true);
  };

  // 预览比赛（不保存）
  const handlePreviewMatches = async () => {
    if (!selectedEvent) return;
    
    // 根据比赛类型确定最少人数要求
    const minPlayers = generateFormData.matchType === 'SINGLES' ? 2 : 4;
    if (generateFormData.players.length < minPlayers) {
      const matchTypeText = generateFormData.matchType === 'SINGLES' ? '单打' : '双打';
      setError(`${matchTypeText}至少需要${minPlayers}名选手`);
      return;
    }

    try {
        const baseUrl = getApiBaseUrl();
      
      // 构建请求数据
      const requestData = {
        players: generateFormData.players,
        matchType: generateFormData.matchType,
        preview: true, // 预览模式
        generateOptions: {}
      };
      
      // 如果totalMatches有效则传递
      if (generateFormData.totalMatches !== null && generateFormData.totalMatches !== undefined && !isNaN(generateFormData.totalMatches)) {
        requestData.totalMatches = Number(generateFormData.totalMatches);
      }
      
      console.log('预览比赛请求数据:', requestData);
      
      const response = await axios.post(
        `${baseUrl}/api/${zone}/events/${selectedEvent._id}/generate-matches`,
        requestData
      );

      if (response.data.success) {
        const { matches, stats } = response.data.data;
        setPreviewMatches(matches);
        setPreviewStats(stats);
        setShowGenerateMatchesForm(false);
        setShowMatchPreview(true);
      } else {
        throw new Error(response.data.message || '预览比赛失败');
      }
    } catch (err) {
      console.error('预览比赛失败:', err);
      setError(err.response?.data?.message || err.message || '预览比赛失败');
      alert(err.response?.data?.message || err.message || '预览比赛失败');
    }
  };

  // 确认生成并保存比赛
  const handleConfirmGenerateMatches = async () => {
    if (!selectedEvent) return;

    try {
        const baseUrl = getApiBaseUrl();
      
      // 构建请求数据
      const requestData = {
        players: generateFormData.players,
        matchType: generateFormData.matchType,
        preview: false, // 保存模式
        generateOptions: {}
      };
      
      // 如果totalMatches有效则传递
      if (generateFormData.totalMatches !== null && generateFormData.totalMatches !== undefined && !isNaN(generateFormData.totalMatches)) {
        requestData.totalMatches = Number(generateFormData.totalMatches);
      }
      
      console.log('确认生成比赛请求数据:', requestData);
      
      const response = await axios.post(
        `${baseUrl}/api/${zone}/events/${selectedEvent._id}/generate-matches`,
        requestData
      );

      if (response.data.success) {
        const { matches, stats } = response.data.data;
        alert(`成功生成 ${matches.length} 场比赛！\n总比赛数: ${stats.totalMatches}\n已完成: ${stats.completedMatches}\n未完成: ${stats.pendingMatches}`);
        setShowMatchPreview(false);
        setPreviewMatches([]);
        setPreviewStats(null);
        // 重新获取比赛列表
        await fetchEventMatches(selectedEvent._id);
        // 重新获取活动列表以更新统计
        const listResponse = await axios.get(`${baseUrl}/api/${zone}/events/with-stats`, {
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (listResponse.data.success) {
          setEvents(listResponse.data.data || []);
          const updatedEvent = listResponse.data.data.find(e => e._id === selectedEvent._id);
          if (updatedEvent) {
            setSelectedEvent(updatedEvent);
          }
        }
      } else {
        throw new Error(response.data.message || '生成比赛失败');
      }
    } catch (err) {
      console.error('确认生成比赛失败:', err);
      setError(err.response?.data?.message || err.message || '生成比赛失败');
      alert(err.response?.data?.message || err.message || '生成比赛失败');
    }
  };

  const handleSettleEvent = async (eventId) => {
    try {
        const baseUrl = getApiBaseUrl();
      console.log('[结算活动] 开始结算，zone:', zone, 'eventId:', eventId);
      const response = await axios.post(`${baseUrl}/api/${zone}/events/${eventId}/settle`);
      if (response.data.success) {
        setEvents(prev => prev.map(event => 
          event._id === eventId 
            ? { ...event, isSettled: true, status: '已结束' } 
            : event
        ));
        
        if (selectedEvent && selectedEvent._id === eventId) {
          setSelectedEvent(prev => ({ ...prev, isSettled: true, status: '已结束' }));
        }
      } else {
        throw new Error(response.data.message || '结算活动失败');
      }
    } catch (err) {
      setError(err.message || '结算活动失败');
      console.error('结算活动失败:', err);
    }
  };

  const handleBackClick = () => {
    if (selectedEvent) {
      setSelectedEvent(null);
      setEventMatches([]);
    } else if (showCreateForm) {
      setShowCreateForm(false);
    }
  };

  // 处理删除比赛按钮点击
  const handleDeleteClick = (e, matchId) => {
    e.stopPropagation(); // 阻止事件冒泡
    setMatchToDelete(matchId);
    setShowConfirmDialog(true);
  };

  // 处理编辑比赛按钮点击
  const handleEditClick = (e, match) => {
    e.stopPropagation(); // 阻止事件冒泡
    setMatchToEdit(match);
    setShowEditForm(true);
  };

  // 完成编辑比赛处理
  const handleEditComplete = (updatedMatch) => {
    // 更新本地状态中的比赛数据
    setEventMatches(prev => 
      prev.map(match => 
        match._id === updatedMatch._id ? updatedMatch : match
      )
    );
    
    // 关闭编辑表单
    setShowEditForm(false);
    setMatchToEdit(null);
  };

  // 取消编辑比赛
  const handleCancelEdit = () => {
    setShowEditForm(false);
    setMatchToEdit(null);
  };

  // 取消删除操作
  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
    setMatchToDelete(null);
  };

  // 确认删除比赛
  const handleConfirmDelete = async () => {
    if (!matchToDelete) return;
    
    try {
        const baseUrl = getApiBaseUrl();
      const response = await axios.delete(`${baseUrl}/api/${zone}/matches/${matchToDelete}`);
      
      if (response.data.success) {
        // 更新本地状态，移除已删除的比赛
        setEventMatches(prev => prev.filter(match => match._id !== matchToDelete));
        
        // 如果比赛数量有变化，同时更新活动状态
        if (selectedEvent) {
          setSelectedEvent(prev => ({
            ...prev,
            matchCount: prev.matchCount > 0 ? prev.matchCount - 1 : 0
          }));
        }
        
        // 关闭确认对话框
        setShowConfirmDialog(false);
        setMatchToDelete(null);
      } else {
        throw new Error(response.data.message || '删除比赛失败');
      }
    } catch (err) {
      console.error('删除比赛失败:', err);
      setError(err.message || '删除比赛失败');
      setShowConfirmDialog(false);
    }
  };

  // 渲染比赛列表
  const renderMatchList = () => {
    if (!eventMatches || eventMatches.length === 0) {
      return <div className="no-matches">暂无比赛记录</div>;
    }

    // 按创建时间从新到旧排序（时间倒序）
    // 最新创建的比赛排在前面，较早创建的排在后面
    const sortedMatches = [...eventMatches].sort((a, b) => {
      // 如果有createdAt字段，按照创建时间从新到旧排序
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return 0; // 如果没有时间字段，保持原顺序
    });

    return (
      <div className="match-list">
        {sortedMatches.map((match, index) => {
          const isTeam1Win = match.winner === 'team1';
          const team1Players = match.teams.team1;
          const team2Players = match.teams.team2;
          const matchType = match.type.toLowerCase();
          
          // 计算从大到小的序号：较新的比赛序号较大，较早的比赛序号较小
          // 例如：如果有5场比赛，最新的显示序号5，最早的显示序号1
          const reverseIndex = sortedMatches.length - index;

          return (
            <div key={match._id} className="match-card">
              <div className="match-sequence-number">{reverseIndex}</div>
              <div className="match-card-content">
                {/* 上半部分：比赛信息 */}
                <div className="match-card-header">
                  {/* 左侧：比赛类型 */}
                  <div className="match-header-left">
                    <span className="match-type large">{matchType === 'singles' ? '单打' : '双打'}</span>
                  </div>
                  
                  {/* 中间：比分 */}
                  <div className="match-header-center">
                    <div className="match-score">
                      {isDefaultScore(match.scores) ? '未开始' : (match.scores || '0-0')}
                    </div>
                  </div>
                  
                  {/* 右侧：标签和添加按钮 */}
                  <div className="match-header-right">
                    {/* 比赛标签显示在比分右侧 */}
                    <div className="match-tags-header">
                      <MatchTagsCompact
                        matchTags={match.matchTags}
                        playerTags={null}
                      />
                    </div>
                    {/* 添加标签按钮 - 靠最右边 */}
                    <button
                      className="add-tag-button-compact"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/mobile/${zone}/matches/${match._id}`);
                      }}
                      title="添加标签"
                      aria-label="添加标签"
                    >
                      <span className="button-icon">+</span>
                    </button>
                    {/* 编辑和删除按钮 - 仅在活动未结算时显示 */}
                    {selectedEvent && !selectedEvent.isSettled && (
                      <div className="match-actions">
                        <button 
                          className="match-delete-button" 
                          onClick={(e) => handleEditClick(e, match)}
                          aria-label="编辑比赛"
                          title="编辑比赛"
                        >
                          <span className="button-icon">✎</span>
                        </button>
                        <button 
                          className="match-delete-button" 
                          onClick={(e) => handleDeleteClick(e, match._id)}
                          aria-label="删除比赛"
                          title="删除比赛"
                        >
                          <span className="button-icon">✕</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* 下半部分：球员信息 - 采用左右布局 */}
                <div className="match-card-players">
                  {/* 双打比赛 - 处理多名球员 */}
                  {matchType === 'doubles' ? (
                    team1Players.map((player1, index) => {
                      const player2 = team2Players[index] || { player: { name: '' }, oldRating: 1500, ratingChange: 0 };
                      return (
                        <div key={`pair-${index}`} className="match-players-container">
                          {/* 左侧球员 */}
                          <div className="player-row">
                            <div className="player-info-line">
                              <div className="player-name">
                                {player1.player.name}
                              </div>
                              {/* 左侧球员的个人标签 - 和名字在同一行 */}
                              {match.playerTags && player1.player && (() => {
                                const playerId = typeof player1.player === 'object' 
                                  ? (player1.player._id || player1.player.id || player1.player)
                                  : player1.player;
                                const playerIdStr = playerId ? String(playerId) : null;
                                if (!playerIdStr) return null;
                                return (
                                  <div className="player-tags-inline">
                                    <MatchTagsCompact
                                      matchTags={null}
                                      playerTags={match.playerTags}
                                      playerId={playerIdStr}
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="player-rating-line">
                              <span className="rating-value" style={{ color: '#666', fontSize: '13px' }}>
                                {player1.oldRating || 1500}
                              </span>
                              {player1.ratingChange !== 0 && (
                                <span className={`rating-change ${player1.ratingChange > 0 ? 'positive' : 'negative'}`} style={{ marginLeft: '4px' }}>
                                  {player1.ratingChange > 0 ? `+${player1.ratingChange}` : player1.ratingChange}
                                </span>
                              )}
                              {player1.newRating && (
                                <span className="new-rating" style={{ marginLeft: '4px' }}>
                                  → {player1.newRating}
                                </span>
                              )}
                              {isMatchStarted(match.scores) && (
                                <span className={`player-result ${isTeam1Win ? 'winner' : 'loser'}`} style={{ marginLeft: '8px' }}>
                                  {isTeam1Win ? '胜' : '负'}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* 队伍分隔线 */}
                          <div className="teams-divider"></div>
                          
                          {/* 右侧球员 */}
                          <div className="player-row">
                            <div className="player-info-line">
                              <div className="player-name">
                                {player2.player.name}
                              </div>
                              {/* 右侧球员的个人标签 - 和名字在同一行 */}
                              {match.playerTags && player2.player && (() => {
                                const playerId = typeof player2.player === 'object' 
                                  ? (player2.player._id || player2.player.id || player2.player)
                                  : player2.player;
                                const playerIdStr = playerId ? String(playerId) : null;
                                if (!playerIdStr) return null;
                                return (
                                  <div className="player-tags-inline">
                                    <MatchTagsCompact
                                      matchTags={null}
                                      playerTags={match.playerTags}
                                      playerId={playerIdStr}
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="player-rating-line">
                              <span className="rating-value" style={{ color: '#666', fontSize: '13px' }}>
                                {player2.oldRating || 1500}
                              </span>
                              {player2.ratingChange !== 0 && (
                                <span className={`rating-change ${player2.ratingChange > 0 ? 'positive' : 'negative'}`} style={{ marginLeft: '4px' }}>
                                  {player2.ratingChange > 0 ? `+${player2.ratingChange}` : player2.ratingChange}
                                </span>
                              )}
                              {player2.newRating && (
                                <span className="new-rating" style={{ marginLeft: '4px' }}>
                                  → {player2.newRating}
                                </span>
                              )}
                              {match.isCompleted && (
                                <span className={`player-result ${isTeam1Win ? 'loser' : 'winner'}`} style={{ marginLeft: '8px' }}>
                                  {isTeam1Win ? '负' : '胜'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                      // 单打比赛 - 只有两名球员
                    <div className="match-players-container">
                      {/* 左侧球员 */}
                      <div className="player-row">
                        <div className="player-info-line">
                          <div className="player-name">
                            {team1Players[0].player.name}
                          </div>
                          {/* 左侧球员的个人标签 - 和名字在同一行 */}
                          {match.playerTags && team1Players[0].player && (() => {
                            const playerId = typeof team1Players[0].player === 'object' 
                              ? (team1Players[0].player._id || team1Players[0].player.id || team1Players[0].player)
                              : team1Players[0].player;
                            const playerIdStr = playerId ? String(playerId) : null;
                            if (!playerIdStr) return null;
                            return (
                              <div className="player-tags-inline">
                                <MatchTagsCompact
                                  matchTags={null}
                                  playerTags={match.playerTags}
                                  playerId={playerIdStr}
                                />
                              </div>
                            );
                          })()}
                        </div>
                        <div className="player-rating-line">
                          <span className="rating-value" style={{ color: '#666', fontSize: '13px' }}>
                            {team1Players[0].oldRating || 1500}
                          </span>
                          {team1Players[0].ratingChange !== 0 && (
                            <span className={`rating-change ${team1Players[0].ratingChange > 0 ? 'positive' : 'negative'}`} style={{ marginLeft: '4px' }}>
                              {team1Players[0].ratingChange > 0 ? `+${team1Players[0].ratingChange}` : team1Players[0].ratingChange}
                            </span>
                          )}
                          {team1Players[0].newRating && (
                            <span className="new-rating" style={{ marginLeft: '4px' }}>
                              → {team1Players[0].newRating}
                            </span>
                          )}
                          {isMatchStarted(match.scores) && (
                            <span className={`player-result ${isTeam1Win ? 'winner' : 'loser'}`} style={{ marginLeft: '8px' }}>
                              {isTeam1Win ? '胜' : '负'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* 队伍分隔线 */}
                      <div className="teams-divider"></div>
                      
                      {/* 右侧球员 */}
                      <div className="player-row">
                        <div className="player-info-line">
                          <div className="player-name">
                            {team2Players[0].player.name}
                          </div>
                          {/* 右侧球员的个人标签 - 和名字在同一行 */}
                          {match.playerTags && team2Players[0].player && (() => {
                            const playerId = typeof team2Players[0].player === 'object' 
                              ? (team2Players[0].player._id || team2Players[0].player.id || team2Players[0].player)
                              : team2Players[0].player;
                            const playerIdStr = playerId ? String(playerId) : null;
                            if (!playerIdStr) return null;
                            return (
                              <div className="player-tags-inline">
                                <MatchTagsCompact
                                  matchTags={null}
                                  playerTags={match.playerTags}
                                  playerId={playerIdStr}
                                />
                              </div>
                            );
                          })()}
                        </div>
                        <div className="player-rating-line">
                          <span className="rating-value" style={{ color: '#666', fontSize: '13px' }}>
                            {team2Players[0].oldRating || 1500}
                          </span>
                          {team2Players[0].ratingChange !== 0 && (
                            <span className={`rating-change ${team2Players[0].ratingChange > 0 ? 'positive' : 'negative'}`} style={{ marginLeft: '4px' }}>
                              {team2Players[0].ratingChange > 0 ? `+${team2Players[0].ratingChange}` : team2Players[0].ratingChange}
                            </span>
                          )}
                          {team2Players[0].newRating && (
                            <span className="new-rating" style={{ marginLeft: '4px' }}>
                              → {team2Players[0].newRating}
                            </span>
                          )}
                          {match.isCompleted && (
                            <span className={`player-result ${isTeam1Win ? 'loser' : 'winner'}`} style={{ marginLeft: '8px' }}>
                              {isTeam1Win ? '负' : '胜'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="mobile-loading">加载中...</div>;
  }

  if (error) {
    return <div className="mobile-error">{error}</div>;
  }

  return (
    <div className="mobile-event-management">
      <div className="mobile-event-header">
        {(showCreateForm || selectedEvent) && (
          <button className="mobile-back-button" onClick={handleBackClick}>
            ←
          </button>
        )}
        <h1>
          {selectedEvent 
            ? selectedEvent.name 
            : (showCreateForm ? '创建活动' : '活动列表')}
        </h1>
        {!selectedEvent && !showCreateForm && (
          <button 
            className="mobile-add-button" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[DEBUG] 添加活动按钮被点击');
              handleCreateEvent();
              console.log('[DEBUG] handleCreateEvent函数调用完成');
            }}
            aria-label="创建活动"
          >
            +
          </button>
        )}
      </div>

      {showCreateForm ? (
        <div className="mobile-event-form-container">
          <form className="mobile-event-form" onSubmit={handleFormSubmit}>
            <div className="mobile-form-group">
              <label htmlFor="name">活动名称</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                placeholder="请输入活动名称"
              />
            </div>
            
            <div className="mobile-form-group">
              <label htmlFor="date">活动日期</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleFormChange}
                required
              />
            </div>
            
            <div className="mobile-form-group">
              <label htmlFor="location">活动地点</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleFormChange}
                required
                placeholder="请输入活动地点"
              />
            </div>
            
            <div className="mobile-form-group">
              <label htmlFor="description">活动描述</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                placeholder="请输入活动描述（可选）"
                rows={4}
              />
            </div>
            
            <div className="mobile-form-actions-inline">
              <button type="button" className="mobile-cancel-button" onClick={handleCancelForm}>
                取消
              </button>
              <button type="submit" className="mobile-submit-button">
                创建活动
              </button>
            </div>
          </form>
        </div>
      ) : selectedEvent ? (
        <>
          <div className="mobile-event-detail">
            <div className="mobile-event-info-item">
              <span className="mobile-event-info-label">活动日期:</span>
              <span className="mobile-event-info-value">{formatDate(selectedEvent.date)}</span>
            </div>
            <div className="mobile-event-info-item">
              <span className="mobile-event-info-label">活动地点:</span>
              <span className="mobile-event-info-value">{selectedEvent.location}</span>
            </div>
            <div className="mobile-event-info-item">
              <span className="mobile-event-info-label">比赛/人数:</span>
              <span className="mobile-event-info-value">{selectedEvent.matchCount} 场 / {selectedEvent.playerCount} 人</span>
            </div>
            <div className="mobile-event-info-item">
              <span className="mobile-event-info-label">积分结算:</span>
              <span className={`mobile-event-settled ${selectedEvent.isSettled ? 'yes' : 'no'}`}>
                {selectedEvent.isSettled ? '已结算' : '未结算'}
              </span>
              <button 
                className="mobile-event-action-button settle inline-button"
                onClick={() => handleSettleEvent(selectedEvent._id)}
                disabled={selectedEvent.isSettled || selectedEvent.matchCount === 0}
              >
                结算积分
              </button>
            </div>
          </div>
          
          <div className="mobile-event-actions">
            <button 
              className="mobile-event-action-button"
              onClick={() => handleAddMatch(selectedEvent._id)}
            >
              添加比赛
            </button>
            <button 
              className="mobile-event-action-button"
              onClick={() => handleOpenGenerateMatches(selectedEvent._id)}
            >
              批量生成比赛
            </button>
          </div>

          {/* 活动总结 - 仅在已结算时显示 */}
          {selectedEvent.isSettled && (
            <EventSummary
              eventId={selectedEvent._id}
              zone={zone}
              isSettled={selectedEvent.isSettled}
              onSummaryUpdate={(summary) => {
                // 总结更新回调（可选）
                console.log('总结已更新:', summary);
              }}
            />
          )}

          <div className="mobile-event-matches">
            <h2>比赛列表</h2>
            {renderMatchList()}
          </div>
        </>
      ) : (
        <div className="mobile-event-list">
          {(() => {
            console.log('[渲染活动列表] 当前events状态:', events);
            console.log('[渲染活动列表] events数量:', events.length);
            console.log('[渲染活动列表] showCreateForm:', showCreateForm);
            console.log('[渲染活动列表] selectedEvent:', selectedEvent);
            
            if (events.length === 0) {
              console.log('[渲染活动列表] 显示"暂无活动数据"');
              return <div className="mobile-no-data">暂无活动数据</div>;
            } else {
              console.log('[渲染活动列表] 开始渲染活动卡片，数量:', events.length);
              return events.map((event) => {
                console.log('[渲染活动列表] 渲染活动:', event.name, 'ID:', event._id);
                return (
                  <div 
                    key={event._id} 
                    className={`mobile-event-card ${event.isSettled ? 'settled' : ''}`}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="mobile-event-card-header">
                      <h2>{event.name}</h2>
                      <span className={`mobile-event-status ${event.status === '已结束' ? 'ended' : event.status === '进行中' ? 'active' : ''}`}>
                        {event.status}
                      </span>
                    </div>
                    <div className="mobile-event-card-content">
                      <div className="mobile-event-card-info">
                        <div className="mobile-event-card-info-item">
                          <i className="calendar-icon">📅</i>
                          <span className="mobile-event-card-info-label">日期:</span>
                          <span className="mobile-event-card-info-value">{formatDate(event.date)}</span>
                        </div>
                        <div className="mobile-event-card-info-item">
                          <i className="location-icon">📍</i>
                          <span className="mobile-event-card-info-label">地点:</span>
                          <span className="mobile-event-card-info-value">{event.location}</span>
                        </div>
                      </div>
                      <div className="mobile-event-card-stats">
                        <div className="mobile-event-card-stat">
                          <span className="mobile-event-card-stat-value">{event.matchCount || 0}</span>
                          <span className="mobile-event-card-stat-label">比赛</span>
                        </div>
                        <div className="mobile-event-card-stat">
                          <span className="mobile-event-card-stat-value">{event.playerCount || 0}</span>
                          <span className="mobile-event-card-stat-label">选手</span>
                        </div>
                        <div className="mobile-event-settled-container">
                          <span className={`mobile-event-settled ${event.isSettled ? 'yes' : 'no'}`}>
                            {event.isSettled ? '已结算' : '未结算'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
            }
          })()}
        </div>
      )}

      {/* 删除确认对话框 */}
      {showConfirmDialog && (
        <div className="modal-overlay">
          <div className="confirm-dialog">
            <h3>确认删除</h3>
            <p>确定要删除这场比赛吗？此操作不可撤销。</p>
            <div className="confirm-dialog-actions">
              <button className="cancel-btn" onClick={handleCancelDelete}>取消</button>
              <button className="confirm-btn" onClick={handleConfirmDelete}>删除</button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑比赛表单 */}
      {showEditForm && matchToEdit && (
        <MobileEditMatch 
          match={matchToEdit} 
          eventId={selectedEvent?._id}
          zone={zone}
          onComplete={handleEditComplete}
          onCancel={handleCancelEdit}
        />
      )}

      {/* 比赛预览弹窗 */}
      {showMatchPreview && previewMatches.length > 0 && (
        <div className="modal-overlay">
          <div className="mobile-form-modal" style={{ maxWidth: '600px' }}>
            <div className="mobile-form-header">
              <h2>比赛预览</h2>
              <button 
                className="mobile-form-close"
                onClick={() => {
                  setShowMatchPreview(false);
                  setPreviewMatches([]);
                  setPreviewStats(null);
                  setShowGenerateMatchesForm(true);
                }}
              >
                ×
              </button>
            </div>
            <div className="mobile-form-content">
              {previewStats && (
                <div className="preview-stats" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4361ee' }}>{previewStats.totalMatches || previewMatches.length}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>总比赛数</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1dd3b0' }}>{previewStats.completedMatches || 0}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>已完成</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffd166' }}>{previewStats.pendingMatches || previewMatches.length}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>未完成</div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="preview-matches" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>比赛列表</h3>
                  <button
                    type="button"
                    onClick={handlePreviewMatches}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      backgroundColor: '#4361ee',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="重新排比赛"
                  >
                    <span>🔄</span>
                    <span>刷新</span>
                  </button>
                </div>
                {previewMatches.map((match, index) => {
                  // 创建球员ID到名字的映射
                  const playerMap = {};
                  availablePlayers.forEach(player => {
                    const playerId = player._id || player.id;
                    if (playerId) {
                      playerMap[playerId] = player.name || '未知';
                    }
                  });
                  
                  // 获取队伍1的球员名字
                  const getPlayerName = (playerId) => {
                    if (!playerId) return '未知';
                    // 如果已经是字符串ID，直接查找
                    if (typeof playerId === 'string') {
                      return playerMap[playerId] || playerId;
                    }
                    // 如果是对象，尝试获取name或id
                    if (typeof playerId === 'object') {
                      return playerId.name || playerMap[playerId._id || playerId.id] || playerId._id || playerId.id || '未知';
                    }
                    return '未知';
                  };
                  
                  const team1 = match.teams.team1.map(p => {
                    const playerId = p.player?._id || p.player?.id || p.player;
                    return getPlayerName(playerId);
                  }).join(', ');
                  
                  const team2 = match.teams.team2.map(p => {
                    const playerId = p.player?._id || p.player?.id || p.player;
                    return getPlayerName(playerId);
                  }).join(', ');
                  
                  const restInfo = match.restPlayers && match.restPlayers.length > 0 
                    ? ` (轮休: ${match.restPlayers.map(p => {
                        // restPlayers 中的格式是 { player: playerId }
                        const playerId = p?.player?._id || p?.player?.id || p?.player || p?._id || p?.id || p;
                        return getPlayerName(playerId);
                      }).join(', ')})` 
                    : '';
                  
                  return (
                    <div key={index} style={{ 
                      padding: '12px', 
                      marginBottom: '10px', 
                      backgroundColor: '#fff', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '6px' 
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>比赛 {index + 1}</span>
                        <span style={{ fontSize: '12px', color: '#999', fontWeight: '400' }}>
                          状态: {isMatchStarted(match.scores) ? '已开始' : '未开始'} | 比分: {isMatchStarted(match.scores) ? (match.scores || '0-0') : '未开始'}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '500' }}>{team1}</span>
                        <span style={{ margin: '0 8px', color: '#999' }}>VS</span>
                        <span style={{ fontWeight: '500' }}>{team2}</span>
                        {restInfo && (
                          <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px', width: '100%', textAlign: 'center' }}>
                            {restInfo}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mobile-form-actions-inline" style={{ marginTop: '20px' }}>
                <button 
                  type="button" 
                  className="mobile-cancel-button" 
                  onClick={() => {
                    setShowMatchPreview(false);
                    setPreviewMatches([]);
                    setPreviewStats(null);
                    setShowGenerateMatchesForm(true);
                  }}
                >
                  取消
                </button>
                <button 
                  type="button" 
                  className="mobile-submit-button"
                  onClick={handleConfirmGenerateMatches}
                >
                  确认生成
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 批量生成比赛表单 */}
      {showGenerateMatchesForm && (
        <div className="modal-overlay">
          <div className="mobile-form-modal">
            <div className="mobile-form-header">
              <h2>批量生成比赛</h2>
              <button 
                className="mobile-form-close"
                onClick={() => setShowGenerateMatchesForm(false)}
              >
                ×
              </button>
            </div>
            <div className="mobile-form-content">
              <div className="mobile-form-group">
                <label>比赛类型</label>
                <div className="match-type-radio-group">
                  <Radio.Group 
                    value={generateFormData.matchType}
                    onChange={(val) => setGenerateFormData(prev => ({ ...prev, matchType: val }))}
                  >
                    <Space justify="around" block>
                      <Radio value="SINGLES" className="match-type-radio">单打</Radio>
                      <Radio value="DOUBLES" className="match-type-radio">双打</Radio>
                    </Space>
                  </Radio.Group>
                </div>
              </div>

              <div className="mobile-form-group">
                <label>选择选手（{generateFormData.matchType === 'SINGLES' ? '至少2人' : '至少4人'}）</label>
                <div className="player-grid-selector">
                  {availablePlayers.map(player => {
                    const playerId = player._id || player.id;
                    const isSelected = generateFormData.players.includes(playerId);
                    const playerLevel = player.level || '新手';
                    
                    return (
                      <div
                        key={playerId}
                        className={`player-grid-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setGenerateFormData(prev => ({
                              ...prev,
                              players: prev.players.filter(id => id !== playerId)
                            }));
                          } else {
                            setGenerateFormData(prev => ({
                              ...prev,
                              players: [...prev.players, playerId]
                            }));
                          }
                        }}
                      >
                        <div className="player-grid-name">{player.name}</div>
                        <div className="player-grid-level">[{playerLevel}]</div>
                      </div>
                    );
                  })}
                </div>
                <div className="form-hint">
                  已选择 {generateFormData.players.length} 名选手
                </div>
              </div>

              <div className="mobile-form-group">
                <label>比赛组局场次说明</label>
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f0f7ff', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.8',
                  color: '#333'
                }}>
                  <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#4361ee' }}>双打比赛规则：</div>
                  <div>• 4人：总共打3场，每人上3场</div>
                  <div>• 5人：总共打5场，每人上4场</div>
                  <div>• 6人：总共打6场，每人上5场</div>
                  <div>• 7人：总共打14场，每人上8场</div>
                  <div>• 8人：总共打14场，每人上7场</div>
                </div>
              </div>

              <div className="mobile-form-actions-inline">
                <button 
                  type="button" 
                  className="mobile-cancel-button" 
                  onClick={() => setShowGenerateMatchesForm(false)}
                >
                  取消
                </button>
                <button 
                  type="button" 
                  className="mobile-submit-button"
                  onClick={handlePreviewMatches}
                  disabled={generateFormData.players.length < (generateFormData.matchType === 'SINGLES' ? 2 : 4)}
                >
                  预览比赛
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileEventManagement;
